import React, { useState, useEffect } from "react";
import SkillCard from "../../components/Common/CommonCard";
import ReusableTable from "../../components/Common/BaseTable";
import productRatingIcon from "../../assets/productRating.png";
import { useNavigate, Routes, Route } from "react-router-dom";
import UnifiedDocumentModal from "../../components/Common/UnifiedDocumentModal";
import ActionMenu from "../../components/ProductDescription/ActionMenu";
import { MoreHorizontal } from "lucide-react";
import talkingToAIIcon from "../../assets/talkingToAI.png";
import Breadcrumb from "../../components/Common/Bredcrump";
import { BreadcrumbItem } from "react-bootstrap";
import KeywordCluster from './KeywordCluster';
import { getBlogPosts, getBlogPostById, deleteBlog, getBlogs } from "../../utils/blog";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";


const Blog = () => {
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [showModal, setShowModal] = useState(false);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [allBlogs, setAllBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [sortType, setSortType] = useState('default');
  const [filterColumn, setFilterColumn] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [blogData, setBlogData] = useState(null);

  useEffect(() => {
    // Only fetch blogs if we haven't fetched yet and not currently loading
    if (!hasFetched && !loading) {
      setLoading(true);
      setError(null);
      getBlogs()
        .then((res) => {
          setAllBlogs(res.data || []);
          setLoading(false);
          setHasFetched(true);
        })
        .catch((err) => {
          setLoading(false);
          setHasFetched(true);
          // Check if it's a 400 or 500 error
          const status = err?.response?.status;
          if (status === 400 || status === 500) {
            setError("No blog data found");
          } else {
            setError(err?.message || "Failed to fetch blogs");
          }
          // Always set empty array to show table with empty message
          setAllBlogs([]);
        });
    }
  }, [hasFetched, loading]);

  useEffect(() => {
    let result = [...allBlogs];
    if (searchTerm) {
      result = result.filter(blog =>
        (blog.title && blog.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (blog.author && blog.author.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    if (sortType === 'newest') {
      result = result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortType === 'oldest') {
      result = result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }
    setFilteredBlogs(result);
  }, [allBlogs, searchTerm, sortType]);

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
  };

  const handleSort = (selectedSort) => {
    setSortType(selectedSort);
  };

  const handleDelete = (selectedRows) => {
    console.log("Delete:", selectedRows);
  };

  const handleDuplicate = (selectedRows) => {
    console.log("Duplicate:", selectedRows);
  };

  const handleMenuClick = (event, rowIndex) => {
    event.stopPropagation();
    const rect = event.target.getBoundingClientRect();
    setMenuPosition({ x: rect.left, y: rect.bottom });
    setMenuOpen(menuOpen === rowIndex ? null : rowIndex);
  };

  const handleMenuOption = (action, rowData) => {
    console.log(`${action} clicked for`, rowData);
    setMenuOpen(null);
  };

  const handleAction = async (action, document) => {
    setActionMenuOpen(null);
    setSelectedDocument(document);

    switch (action) {
      case "preview":
        const platform = document.platform || "Shopify"; 
        const isShopifyBlog = platform === "Shopify" || document.blog_id !== undefined;
        const blogId = document.blog_id; 
        
        if (isShopifyBlog) {
          navigate(`/preview-blog/${document.id}`, {
            state: {
              contentType: "blog",
              blogSource: "Shopify",
              blogId: blogId,
              backUrl: "/skills/blog"
            }
          });
        } else {
         
          navigate(`/skills/blog/blog-preview/${document.id}`, { 
            state: { blogId: document.id } 
          });
        }
        break;
      case "edit":
        setBlogData(document)
        setShowDocumentModal(true);
        break;
      case "publish":
        console.log("Publish:", document);
        break;
      case "delete":
        try {
          await deleteBlog(document.id);
          setAllBlogs(prevBlogs => prevBlogs.filter(blog => blog.id !== document.id));
          toast.success("Blog deleted successfully!");
        } catch (err) {
          console.error("Failed to delete blog:", err);
          toast.error(err.message || "Failed to delete blog.");
        }
        break;
      default:
        break;
    }
  };

  const skills = [ { title: "Blog", icon: talkingToAIIcon },];
  const columns = [
    {
      id: "selection",
      header: ({ table }) => (
        <input
          type="checkbox"
          checked={table.getIsAllPageRowsSelected()}
          onChange={() => table.toggleAllPageRowsSelected()}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          onChange={() => row.toggleSelected()}
        />
      ),
      size: "5%",
    },
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }) => {
        const title = row.original.title;
        const name = row.original.name;
        const about = row.original.about;
        return title || name || about || "Untitled";
      },
    },
    {
      accessorKey: "step",
      header: "Step",
      cell: ({ row }) => {
        const data = row.original;
        if (data?.final_blog && Object.keys(data?.final_blog).length > 0) return "Final Blog";
        if (data?.outlines && Object.keys(data?.outlines).length > 0) return "Topic Map";
        return "N/A";
      },
    },
    {
      accessorKey: "platform",
      header: "Platform",
      cell: ({ getValue }) => {
        const platform = getValue();
        return platform || "Shopify";
      },
    },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => {
          const rowData = row.original;
          const status = rowData.status;

          let statusText = "Draft";
          let statusClass = "status-draft";
          let dotColor = "grey";

          if (status) {
            statusText = status;
            statusClass = status.toLowerCase() === "visible" || status.toLowerCase() === "public" ? "status-published" : "status-draft";
            dotColor = statusClass === "status-published" ? "green" : "grey";
          } else if (rowData.published_at) {
            statusText = "Published";
            statusClass = "status-published";
            dotColor = "green";
          }

          return (
            <span className={`status-badge ${statusClass}`}>
              <span style={{ color: dotColor, marginRight: '5px' }}>•</span>
              {statusText}
            </span>
          );
        },
      },
    {
      accessorKey: "created_at",
      header: "Created At",
      cell: ({ getValue }) => {
        const rawDate = getValue();
        const formattedDate = new Date(rawDate).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
        return formattedDate;
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const handleActionClick = (e) => {
          e.stopPropagation();
          setActionMenuOpen(actionMenuOpen === row.original.id ? null : row.original.id);
          
          
          const closeMenu = (event) => {
            if (!event.target.closest('.position-relative')) {
              setActionMenuOpen(null);
              document.removeEventListener('click', closeMenu);
            }
          };
          
          
          setTimeout(() => {
            document.addEventListener('click', closeMenu);
          }, 0);
        };

        return (
          <div className="position-relative">
            <button
              className="action-button btn"
              onClick={handleActionClick}
            >
              <MoreHorizontal size={20} />
            </button>
            {actionMenuOpen === row.original.id && (
              <ActionMenu
                onAction={(action) => handleAction(action, row.original)}
              />
            )}
          </div>
        );
      },
    },
  ];

  const handleSkillClick = (title) => {
    console.log("HandleSkillClick");
    // navigate("/product-description");
    setShowDocumentModal(true)
  };

  return (
    <div>
      {/* <h3 className="title-content">Skills {">"} Blog</h3> */}
      <Breadcrumb />
      
      <div
        className="skills-container"
        style={{ display: "flex", gap: "16px", marginTop: "5px" }}
      >
        {skills.map((skill, index) => (
          <SkillCard
            key={skill?.title}
            icon={skill.icon}
            title={skill.title}
            onClick={() => handleSkillClick(skill.title)}
          />
        ))}
      </div>
      <div className="headertop d-flex justify-content-between align-items-center">
      <div className="title-table-header">Generated Content</div>
      <button 
        className="btn text-light" 
        style={{backgroundColor:'var(--primary)'}}
        onClick={() => setShowDocumentModal(true)}
      >+ Generate Blog</button>
      </div>
      <div className="mt-3 border rounded">
        <ReusableTable
          data={filteredBlogs || []}
          columns={columns}
          onSearch={handleSearch}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onSort={handleSort}
          gridView={false}
          isLoading={loading}
        />
      </div>
      <UnifiedDocumentModal
        show={showDocumentModal}
        onHide={() => setShowDocumentModal(false)}
        initialTags=""
        initialDocList=""
        blogData={blogData}
      />
      <Routes>
        <Route path="/skills/blog/keyword-cluster" element={<KeywordCluster />} />
      </Routes>
      <ToastContainer />
    </div>
  );
};

export default Blog;
